import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./CashBook.css";
import { TableRowsSkeleton } from "../../Component/Skeleton";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

const formatINR = (val) => {
  const num = Number(val || 0);
  if (!num || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function CashBook() {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name: selectedCompany?.Name || selectedCompany?.name || selectedCompany?.CompanyName || "DAZY TOURE TRAVEL'S",
      address: selectedCompany?.Address || selectedCompany?.address || "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Date helpers
  const defaultDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return {
      fromDate: `${y}-${m}-01`,
      toDate: `${y}-${m}-${d}`,
    };
  }, []);

  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const [viewMode, setViewMode] = useState("detail"); // "short" or "detail"
  const [showNarration, setShowNarration] = useState(false);
  const [narrationFilter, setNarrationFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Data states
  const [vouchers, setVouchers] = useState([]);
  const [summary, setSummary] = useState({
    totalVouchers: 0,
    totalDebit: 0,
    totalCredit: 0,
    totalEntries: 0,
  });
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Live timestamp for "As on" badge
  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Fetch Cash Book data (Zero API call for toggle or narration search)
  const fetchCashBookData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {
        fromDate,
        toDate,
      };

      const res = await axios.get(`${BASE_API_URL}/reports/cash-book/${companyId}`, { params });
      if (res.data?.success) {
        setVouchers(res.data.data || []);
        setSummary(
          res.data.summary || { totalVouchers: 0, totalDebit: 0, totalCredit: 0, totalEntries: 0 }
        );
        setCurrentPage(1);
      } else {
        setVouchers([]);
        setSummary({ totalVouchers: 0, totalDebit: 0, totalCredit: 0, totalEntries: 0 });
        showToast.info("No cash vouchers found");
      }
    } catch (err) {
      console.error("Failed to load cash book:", err);
      setVouchers([]);
      showToast.error("Failed to load cash book data");
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, fromDate, toDate]);

  useEffect(() => {
    if (companyId) {
      fetchCashBookData();
    }
  }, [companyId, fetchCashBookData]);

  const handleReset = () => {
    setFromDate(defaultDates.fromDate);
    setToDate(defaultDates.toDate);
    setViewMode("detail");
    setShowNarration(false);
    setNarrationFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter vouchers client-side (search box & narration search - 100% in-memory without API call)
  const filteredVouchers = useMemo(() => {
    let result = vouchers;

    // Client-side narration filter
    if (showNarration && narrationFilter.trim()) {
      const nTerm = narrationFilter.toLowerCase().trim();
      result = result.filter((v) => v.narration && v.narration.toLowerCase().includes(nTerm));
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((v) => {
        const matchVoucher = v.voucherNo && v.voucherNo.toLowerCase().includes(term);
        const matchNarration = v.narration && v.narration.toLowerCase().includes(term);
        const matchLedger =
          v.lines &&
          v.lines.some((l) => l.ledgerName && l.ledgerName.toLowerCase().includes(term));
        return matchVoucher || matchNarration || matchLedger;
      });
    }

    return result;
  }, [vouchers, showNarration, narrationFilter, searchTerm]);

  // Paginated records
  const totalPages = Math.ceil(filteredVouchers.length / rowsPerPage) || 1;
  const paginatedVouchers = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredVouchers.slice(start, start + rowsPerPage);
  }, [filteredVouchers, currentPage, rowsPerPage]);


  // Totals for filtered vouchers
  const filteredTotals = useMemo(() => {
    let totDebit = 0;
    let totCredit = 0;
    filteredVouchers.forEach((v) => {
      totDebit += Number(v.debitTotal || 0);
      totCredit += Number(v.creditTotal || 0);
    });
    return {
      debit: Math.round(totDebit * 100) / 100,
      credit: Math.round(totCredit * 100) / 100,
    };
  }, [filteredVouchers]);

  // Export to Excel
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "CASH BOOK";
      const periodSubtitle = `Period: ${fromDate} to ${toDate}  |  Generated: ${asOnTime}`;

      const headers = ["Date", "Voucher No.", "Particulars / Narration", "Debit (INR)", "Credit (INR)"];

      const dataRows = [];
      filteredVouchers.forEach((v) => {
        const debitLines = (v.lines || []).filter((l) => l.type === "Dr");
        const creditLines = (v.lines || []).filter((l) => l.type === "Cr");

        if (debitLines.length === 0 && creditLines.length === 0) {
          dataRows.push([
            v.date,
            v.voucherNo,
            v.narration || "General Entry",
            Number(v.debitTotal) > 0 ? Number(v.debitTotal) : "",
            "",
          ]);
          if (Number(v.creditTotal) > 0) {
            dataRows.push([
              "",
              "",
              "  General Entry",
              "",
              Number(v.creditTotal),
            ]);
          }
        } else {
          if (debitLines.length === 0) {
            dataRows.push([
              v.date,
              v.voucherNo,
              v.narration || "General Entry",
              Number(v.debitTotal) > 0 ? Number(v.debitTotal) : "",
              "",
            ]);
          } else {
            debitLines.forEach((dr, idx) => {
              const drAmt = Number(dr.debit) || Number(v.debitTotal) || 0;
              dataRows.push([
                idx === 0 ? v.date : "",
                idx === 0 ? v.voucherNo : "",
                dr.ledgerName,
                drAmt > 0 ? drAmt : "",
                "",
              ]);
            });
          }

          if (viewMode === "detail") {
            creditLines.forEach((cr, idx) => {
              const crAmt = Number(cr.credit) || Number(v.creditTotal) || 0;
              dataRows.push([
                debitLines.length === 0 && idx === 0 ? v.date : "",
                debitLines.length === 0 && idx === 0 ? v.voucherNo : "",
                `  ${cr.ledgerName}`,
                "",
                crAmt > 0 ? crAmt : "",
              ]);
            });
          }
        }

        if (showNarration && v.narration) {
          dataRows.push([
            "",
            "",
            `  (Narration: ${v.narration})`,
            "",
            "",
          ]);
        }
      });

      const totalRow = [
        "Total",
        "",
        "",
        Number(filteredTotals.debit) || 0,
        Number(filteredTotals.credit) || 0,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      ];

      worksheet["!cols"] = [
        { wch: 15 }, // Date
        { wch: 15 }, // Voucher No
        { wch: 55 }, // Particulars
        { wch: 18 }, // Debit
        { wch: 18 }, // Credit
      ];

      // Format number cells in worksheet with standard Indian 2-decimal format
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = 5; R <= range.e.r; ++R) {
        const cellDr = worksheet[XLSX.utils.encode_cell({ r: R, c: 3 })];
        if (cellDr && typeof cellDr.v === "number") {
          cellDr.z = "#,##0.00";
        }
        const cellCr = worksheet[XLSX.utils.encode_cell({ r: R, c: 4 })];
        if (cellCr && typeof cellCr.v === "number") {
          cellCr.z = "#,##0.00";
        }
      }


      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Cash_Book");
      const fileName = `Cash_Book_${fromDate}_to_${toDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "CASH BOOK";

      // 1. Company Name Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Divider Line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Period: ${fromDate} to ${toDate}`, 14, 36);
      doc.text(`Generated: ${asOnTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Divider Line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const tableRows = [];
      filteredVouchers.forEach((v) => {
        const debitLines = (v.lines || []).filter((l) => l.type === "Dr");
        const creditLines = (v.lines || []).filter((l) => l.type === "Cr");
        const firstDebit = debitLines[0];
        const firstCredit = creditLines[0];

        const linesArr = [];
        if (firstDebit) linesArr.push(firstDebit.ledgerName);
        if (viewMode === "detail" && firstCredit) linesArr.push(`  ${firstCredit.ledgerName}`);
        if (!firstDebit && firstCredit) linesArr.push(firstCredit.ledgerName);
        if (!firstDebit && !firstCredit) linesArr.push(v.narration || "General Entry");
        const linesStr = linesArr.join("\n");
        const narrationStr = showNarration && v.narration ? `\n  Narration: ${v.narration}` : "";

        const debitStr = firstDebit && v.debitTotal > 0 ? formatINR(v.debitTotal) : "";
        const creditStr = viewMode === "detail" && firstCredit && v.creditTotal > 0 ? formatINR(v.creditTotal) : "";

        const drCol = (viewMode === "detail" && firstCredit) ? (debitStr ? `${debitStr}\n` : "") : debitStr;
        const crCol = (viewMode === "detail" && firstDebit) ? (creditStr ? `\n${creditStr}` : "") : (viewMode === "detail" ? creditStr : "");

        tableRows.push([
          v.date,
          v.voucherNo,
          linesStr + narrationStr,
          drCol,
          crCol,
        ]);
      });

      tableRows.push([
        "",
        "Total",
        "",
        formatINR(filteredTotals.debit),
        formatINR(filteredTotals.credit),
      ]);

      autoTable(doc, {
        head: [["Date", "Voucher No.", "Particulars", "Debit (INR)", "Credit (INR)"]],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8.5,
          halign: "center",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: 30,
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 24 },
          1: { halign: "center", cellWidth: 24 },
          2: { halign: "left" },
          3: { halign: "right", cellWidth: 32 },
          4: { halign: "right", cellWidth: 32 },
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text("etour - Design & Develop by Response Infoway", 14, doc.internal.pageSize.getHeight() - 8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
        },
      });

      const fileName = `Cash_Book_${fromDate}_to_${toDate}.pdf`;
      doc.save(fileName);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="cb_page_wrapper">
      {/* Formal Printable Header */}
      <div className="cb_print_header d-none d-print-block text-center mb-3">
        <h2 className="mb-0 fw-bold text-uppercase" style={{ fontSize: "18px", color: "#000000", letterSpacing: "0.5px" }}>
          {companyInfo.name}
        </h2>
        <div style={{ fontSize: "11px", color: "#333333", marginTop: "2px" }}>
          {[companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ")}
        </div>
        <div className="fw-bold mt-2" style={{ fontSize: "13px", color: "#000000" }}>
          CASH BOOK
        </div>
        <div style={{ fontSize: "10.5px", color: "#555555" }}>
          Period: {fromDate} to {toDate} | As on: {asOnTime}
        </div>
      </div>

      {/* Top Header & Breadcrumb */}
      <div className="cb_header_section">
        <div className="cb_breadcrumb_bar">
          Reports <span>&gt;</span> <strong>Cash Book</strong>
        </div>
        <div className="cb_as_on_badge">
          <FaClock />
          As on: {asOnTime}
        </div>
      </div>

      {/* Filter Card (From Date, To Date, View, Reset) - Matches Image 2 */}
      <div className="cb_filter_card">
        <div className="cb_filter_bar">
          {/* From Date */}
          <div className="cb_filter_group">
            <label className="cb_filter_label">
              From Date<span className="required_star">*</span>
            </label>
            <input
              type="date"
              className="filter-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* To Date */}
          <div className="cb_filter_group">
            <label className="cb_filter_label">
              To Date<span className="required_star">*</span>
            </label>
            <input
              type="date"
              className="filter-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: 130 }}
            />
          </div>

          {/* Format Toggle (Short / Detail) */}
          <div className="cb_filter_group">
            <label className="cb_filter_label">Format:</label>
            <div className="cb_format_toggle">
              <button
                type="button"
                className={`cb_format_btn ${viewMode === "short" ? "active" : ""}`}
                onClick={() => setViewMode("short")}
                title="Short View: Show only Debit ledger & amount (hide Cr's)"
              >
                Short
              </button>
              <button
                type="button"
                className={`cb_format_btn ${viewMode === "detail" ? "active" : ""}`}
                onClick={() => setViewMode("detail")}
                title="Detailed View: Show both Debit and Credit ledgers & amounts"
              >
                Detail
              </button>
            </div>
          </div>

          {/* Narration Filter */}
          <div className="cb_filter_group cb_narration_box">
            <label className="cb_filter_label d-flex align-items-center gap-1" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                className="cb_checkbox"
                checked={showNarration}
                onChange={(e) => {
                  setShowNarration(e.target.checked);
                  if (!e.target.checked) setNarrationFilter("");
                }}
              />
              Narration
            </label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search narration..."
              value={narrationFilter}
              disabled={!showNarration}
              onChange={(e) => setNarrationFilter(e.target.value)}
              style={{ width: 140, opacity: showNarration ? 1 : 0.6 }}
            />
          </div>

          {/* Action Buttons */}
          <div className="cb_filter_buttons">
            <button
              type="button"
              className="cb_btn_apply"
              onClick={fetchCashBookData}
              disabled={loading}
            >
              View
            </button>
            <button
              type="button"
              className="cb_btn_reset"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Card: Toolbar + Table + Pagination */}
      <div className="cb_content_card">
        {/* Table Toolbar */}
        <div className="cb_toolbar">
          <h3 className="cb_table_title">
            Cash Book ({fromDate} to {toDate})
          </h3>

          <div className="cb_toolbar_right">
            <div className="cb_search_box">
              <FaSearch className="cb_search_icon" />
              <input
                type="text"
                className="cb_search_input"
                placeholder="Search invoice no. or party..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <button
              type="button"
              className="cb_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>
            <button
              type="button"
              className="cb_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              <FaFilePdf />
              Export PDF
            </button>
            <button
              type="button"
              className="cb_export_btn btn_print"
              onClick={handlePrint}
              title="Print Summary"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="cb_table_wrapper">
          <table className="cb_custom_table">
            <thead>
              <tr>
                <th style={{ width: "100px", textAlign: "center" }}>Date</th>
                <th style={{ width: "110px", textAlign: "center" }}>Voucher No.</th>
                <th>Particulars</th>
                <th style={{ width: "140px", textAlign: "right" }}>Debit (₹)</th>
                <th style={{ width: "140px", textAlign: "right" }}>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableRowsSkeleton columns={5} rows={5} />
              ) : paginatedVouchers.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#a0aec0" }}>
                    No cash vouchers found for the selected period.
                  </td>
                </tr>
              ) : (
                paginatedVouchers.map((v) => {
                  const debitLines = (v.lines || []).filter((l) => l.type === "Dr");
                  const creditLines = (v.lines || []).filter((l) => l.type === "Cr");
                  const firstDebit = debitLines[0];
                  const firstCredit = creditLines[0];

                  return (
                    <tr
                      key={v.voucherId}
                      onClick={() =>
                        navigate(`/transactions/modify-voucher?voucherId=${v.voucherId}`)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ textAlign: "center", color: "#4a5568", whiteSpace: "nowrap" }}>
                        {v.date}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "600", whiteSpace: "nowrap" }}>
                        <span
                          className="cb_voucher_click_link"
                          title="Click to view & modify this voucher in Transactions"
                          style={{
                            cursor: "pointer",
                            color: "var(--primary-teal)",
                            textDecoration: "none",
                            fontWeight: 700,
                          }}
                        >
                          {v.voucherNo}
                        </span>
                      </td>
                      <td>
                        <div className="cb_particulars_cell">
                          {/* First Debit Leg */}
                          {firstDebit && (
                            <div className="cb_debit_ledger">
                              {firstDebit.ledgerName}
                            </div>
                          )}

                          {/* First Credit Leg (Unhidden in detail mode, hidden in short mode) */}
                          {viewMode === "detail" && firstCredit && (
                            <div className="cb_credit_ledger">
                              {firstCredit.ledgerName}
                            </div>
                          )}

                          {!firstDebit && firstCredit && (
                            <div className="cb_debit_ledger">
                              {firstCredit.ledgerName}
                            </div>
                          )}

                          {!firstDebit && !firstCredit && (
                            <div className="cb_debit_ledger">
                              {v.narration || "Cash Entry"}
                            </div>
                          )}

                          {/* Narration */}
                          {showNarration && v.narration && (
                            <div className="cb_narration_line">
                              Narration: {v.narration}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="cb_table_num">
                        <div className="cb_amount_cell">
                          {firstDebit && (
                            <div className="cb_debit_amt">
                              {v.debitTotal > 0 ? formatINR(v.debitTotal) : ""}
                            </div>
                          )}
                          {viewMode === "detail" && firstCredit && <div className="cb_dash_amt"></div>}
                          {showNarration && v.narration && <div className="cb_amt_placeholder">&nbsp;</div>}
                          {!firstDebit && !firstCredit && (
                            <div className="cb_debit_amt">
                              {v.debitTotal > 0 ? formatINR(v.debitTotal) : ""}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="cb_table_num">
                        <div className="cb_amount_cell">
                          {viewMode === "detail" && (
                            <>
                              {firstDebit && <div className="cb_dash_amt"></div>}
                              {firstCredit && (
                                <div className="cb_credit_amt">
                                  {v.creditTotal > 0 ? formatINR(v.creditTotal) : ""}
                                </div>
                              )}
                              {!firstDebit && !firstCredit && (
                                <div className="cb_credit_amt">
                                  {v.creditTotal > 0 ? formatINR(v.creditTotal) : ""}
                                </div>
                              )}
                            </>
                          )}
                          {showNarration && v.narration && <div className="cb_amt_placeholder">&nbsp;</div>}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredVouchers.length > 0 && (
              <tfoot>
                <tr className="cb_table_total_row">
                  <td style={{ textAlign: "center" }}></td>
                  <td style={{ textAlign: "center" }}></td>
                  <td style={{ fontWeight: "700" }}>Total</td>
                  <td className="cb_table_num" style={{ fontWeight: "700", color: "#166534" }}>
                    {formatINR(filteredTotals.debit)}
                  </td>
                  <td className="cb_table_num" style={{ fontWeight: "700", color: "#166534" }}>
                    {formatINR(filteredTotals.credit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Row */}
        {filteredVouchers.length > 0 && (
          <div className="cb_pagination_row">
            <div>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredVouchers.length)} of{" "}
              {filteredVouchers.length} vouchers
            </div>

            <div className="cb_page_controls">
              <button
                type="button"
                className="cb_page_btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span style={{ padding: "0 3px" }}>...</span>}
                      <button
                        type="button"
                        className={`cb_page_btn ${currentPage === p ? "active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                type="button"
                className="cb_page_btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DEDICATED PRINT AREA */}
      <div className="cb_print_area">
        <div style={{ textAlign: "center", marginBottom: "8px" }}>
          <h2 style={{ margin: "0 0 2px 0", fontSize: "16px", fontWeight: "bold", textTransform: "uppercase" }}>
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", fontWeight: "bold", color: "#000000" }}>
            CASH BOOK
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>Period:</strong> {fromDate} to {toDate}  |  <strong>Format:</strong> {viewMode === "short" ? "Short" : "Detail"}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime}  |  <strong>Vouchers:</strong> {filteredVouchers.length}
          </div>
        </div>

        <table className="cb_print_table">
          <thead>
            <tr>
              <th style={{ width: "12%", textAlign: "center" }}>Date</th>
              <th style={{ width: "14%", textAlign: "center" }}>Voucher No.</th>
              <th style={{ width: "46%" }}>Particulars</th>
              <th style={{ width: "14%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "14%", textAlign: "right" }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filteredVouchers.map((v, idx) => {
              const debitLines = (v.lines || []).filter((l) => l.type === "Dr");
              const creditLines = (v.lines || []).filter((l) => l.type === "Cr");
              const firstDebit = debitLines[0];
              const firstCredit = creditLines[0];

              return (
                <tr key={v.voucherId || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                  <td style={{ textAlign: "center", verticalAlign: "top" }}>{v.date}</td>
                  <td style={{ textAlign: "center", fontWeight: 600, verticalAlign: "top" }}>{v.voucherNo}</td>
                  <td style={{ verticalAlign: "top" }}>
                    <div className="cb_particulars_cell">
                      {firstDebit && (
                        <div className="cb_debit_ledger" style={{ fontWeight: 600 }}>
                          {firstDebit.ledgerName}
                        </div>
                      )}
                      {viewMode === "detail" && firstCredit && (
                        <div className="cb_credit_ledger" style={{ paddingLeft: "14px", color: "#1a202c", fontWeight: 600 }}>
                          {firstCredit.ledgerName}
                        </div>
                      )}
                      {!firstDebit && firstCredit && (
                        <div className="cb_debit_ledger" style={{ fontWeight: 600 }}>
                          {firstCredit.ledgerName}
                        </div>
                      )}
                      {!firstDebit && !firstCredit && (
                        <div style={{ fontWeight: 600 }}>
                          {v.narration || "Cash Entry"}
                        </div>
                      )}
                      {showNarration && v.narration && (
                        <div style={{ paddingLeft: "14px", fontSize: "9px", fontStyle: "italic", color: "#555" }}>
                          Narration: {v.narration}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "right", verticalAlign: "top" }}>
                    <div className="cb_amount_cell">
                      {firstDebit && (
                        <div className="cb_debit_amt">
                          {v.debitTotal > 0 ? formatINR(v.debitTotal) : "-"}
                        </div>
                      )}
                      {viewMode === "detail" && firstCredit && <div className="cb_dash_amt"></div>}
                      {showNarration && v.narration && <div className="cb_amt_placeholder">&nbsp;</div>}
                      {!firstDebit && !firstCredit && (
                        <div className="cb_debit_amt">
                          {v.debitTotal > 0 ? formatINR(v.debitTotal) : ""}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "right", verticalAlign: "top" }}>
                    <div className="cb_amount_cell">
                      {viewMode === "detail" && (
                        <>
                          {firstDebit && <div className="cb_dash_amt"></div>}
                          {firstCredit && (
                            <div className="cb_credit_amt">
                              {v.creditTotal > 0 ? formatINR(v.creditTotal) : ""}
                            </div>
                          )}
                          {!firstDebit && !firstCredit && (
                            <div className="cb_credit_amt">
                              {v.creditTotal > 0 ? formatINR(v.creditTotal) : "-"}
                            </div>
                          )}
                        </>
                      )}
                      {showNarration && v.narration && <div className="cb_amt_placeholder">&nbsp;</div>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td style={{ textAlign: "center" }}></td>
              <td style={{ textAlign: "center" }}></td>
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(filteredTotals.debit)}</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(filteredTotals.credit)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="cb_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
